import { Alert, BrandLogo, Card } from '@catering/ui';
import { useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { apiRequest } from '../../api/client';

/**
 * Reached straight from the unsubscribe link in a notification email
 * (`unsubscribe-token.service.ts`'s `buildUrl`), never from inside the
 * app — no session exists yet, so this route lives outside `protectedRoute`
 * (`router.tsx`, same place as `/login`).
 *
 * `POST /notifications/unsubscribe` is deliberately anonymous
 * (`notifications.controller.ts`'s own comment); every token failure —
 * missing, malformed, expired, unknown — answers 422 with the same
 * `NOTIFICATION_UNSUBSCRIBE_TOKEN_INVALID` code, so this screen shows one
 * honest "inválido o vencido" message for the whole class rather than
 * guessing at a distinction the backend does not make.
 */
export function UnsubscribePage(): JSX.Element {
  const { token } = useSearch({ from: '/notifications/unsubscribe' });

  const result = useQuery({
    queryKey: ['notifications-unsubscribe', token],
    enabled: token !== undefined,
    retry: false,
    queryFn: () =>
      apiRequest<{ unsubscribed: true }>(
        `/notifications/unsubscribe?token=${encodeURIComponent(token ?? '')}`,
        { method: 'POST' },
      ),
  });

  const isInvalid = token === undefined || result.isError;

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Back-office</span>
        </div>

        <Card variant="elevated" className="w-full">
          <div className="flex flex-col gap-4">
            <h1 className="text-lg font-semibold text-slate-900">Baja de notificaciones</h1>

            {result.isPending && token !== undefined ? (
              <p className="text-sm text-slate-500">Procesando…</p>
            ) : null}

            {isInvalid ? (
              <Alert tone="danger">
                Enlace inválido o vencido. Si querés dejar de recibir notificaciones, entrá al panel
                de administración y actualizá tus preferencias.
              </Alert>
            ) : null}

            {!isInvalid && result.isSuccess ? (
              <Alert tone="success">Te diste de baja de las notificaciones por correo.</Alert>
            ) : null}
          </div>
        </Card>
      </div>
    </main>
  );
}
